import { createContext, type FC, type PropsWithChildren, useCallback, useContext, useEffect, useState } from "react";

import { useTrackActivity } from "src/activity/useTrackActivity";
import { type AdvisorChat, useAdvisorChat } from "src/advisor/useAdvisorChat";
import { usePermissions } from "src/auth/usePermissions";
import { theme } from "src/theme/theme";

const OPEN_KEY = "sa.advisor.open";

/** Only the desktop column reopens by itself; on phones the advisor is a full-screen view the user opens. */
const DESKTOP_MEDIA = theme.breakpoints.up("md").replace(/^@media\s*/, "");

const readOpen = () => {
	try {
		return localStorage.getItem(OPEN_KEY) === "true" && window.matchMedia(DESKTOP_MEDIA).matches;
	} catch {
		return false;
	}
};

const writeOpen = (open: boolean) => {
	try {
		localStorage.setItem(OPEN_KEY, String(open));
	} catch {
		// Storage can be unavailable (private mode); the advisor then starts closed.
	}
};

interface AdvisorState {
	/** Whether the caller may use the advisor at all; every entry point hides when not. */
	available: boolean;
	open: boolean;
	openAdvisor: () => void;
	closeAdvisor: () => void;
	toggleAdvisor: () => void;
	chat: AdvisorChat;
}

const AdvisorContext = createContext<AdvisorState | null>(null);

/**
 * Advisor open state and the conversation, held by the shell so the conversation survives
 * navigation and collapsing the column. Whether the advisor was open is remembered per browser.
 */
export const AdvisorProvider: FC<PropsWithChildren> = ({ children }) => {
	const track = useTrackActivity();
	const chat = useAdvisorChat();
	const { canUseAdvisor: available } = usePermissions();
	const [requested, setOpen] = useState(readOpen);
	// Closed whenever the caller cannot use it, including an advisor already open when a grant is revoked.
	const open = requested && available;

	useEffect(() => {
		if (!available) return;
		writeOpen(open);
		if (open) track({ type: "advisor.opened", data: {} });
	}, [available, open, track]);

	const openAdvisor = useCallback(() => {
		if (available) setOpen(true);
	}, [available]);
	const closeAdvisor = useCallback(() => setOpen(false), []);
	const toggleAdvisor = useCallback(() => {
		if (available) setOpen(current => !current);
	}, [available]);

	return (
		<AdvisorContext value={{ available, open, openAdvisor, closeAdvisor, toggleAdvisor, chat }}>
			{children}
		</AdvisorContext>
	);
};

export const useAdvisor = (): AdvisorState => {
	const state = useContext(AdvisorContext);
	if (!state) throw new Error("useAdvisor must be used inside <AdvisorProvider>");
	return state;
};

/** Opens the advisor: the left column on desktop, a full-screen view on phones. */
export const useOpenAdvisor = () => useAdvisor().openAdvisor;
